/**
 * T-1g-READ — last-known read cache.
 *
 * A no-WiFi field app (invariant 5) cannot make its Decisions and Action List
 * tabs useless the moment the radio drops. The cache is what those tabs show
 * when a read fails: the last view the server actually gave us, with the
 * timestamp it was given, so the UI can say how old it is rather than passing
 * stale data off as current.
 *
 * Stored in the SAME IndexedDB the outbox uses (via `meta`), so it survives a
 * force-quit and a cold launch — the two moments a super is most likely to be
 * offline. Keyed per project: switching projects must never show another job's
 * decisions, even for one frame.
 */

import { getMeta, setMeta } from "../outbox/db";

export type ReadSurface = "decisions" | "actions" | "logs";

export interface CachedRead<T> {
  items: T[];
  /** Epoch ms when the SERVER produced this view (not when it was displayed). */
  fetchedAt: number;
}

function cacheKey(surface: ReadSurface, project_ref: string): string {
  return `read-cache:${surface}:${project_ref}`;
}

export async function readCacheGet<T>(
  surface: ReadSurface,
  project_ref: string,
): Promise<CachedRead<T> | null> {
  try {
    const raw = (await getMeta(cacheKey(surface, project_ref))) as CachedRead<T> | undefined;
    if (!raw || !Array.isArray(raw.items) || typeof raw.fetchedAt !== "number") return null;
    return raw;
  } catch {
    // A cache miss must never break a tab — the surface simply has nothing
    // to fall back to and says so.
    return null;
  }
}

export async function readCachePut<T>(
  surface: ReadSurface,
  project_ref: string,
  items: T[],
  fetchedAt: number = Date.now(),
): Promise<void> {
  try {
    await setMeta(cacheKey(surface, project_ref), { items, fetchedAt });
  } catch {
    // Best-effort: failing to cache degrades the next offline launch, but must
    // not fail the read that just succeeded.
  }
}

/** Remove an authoritatively discarded identity from the durable fallback view. */
export async function readCacheRemoveItem(
  surface: ReadSurface,
  project_ref: string,
  id: string,
): Promise<void> {
  const cached = await readCacheGet<{ id: string }>(surface, project_ref);
  if (!cached) return;
  await readCachePut(
    surface,
    project_ref,
    cached.items.filter((item) => item.id !== id),
    cached.fetchedAt,
  );
}
